import { chain, isEmpty, isObject, pick, get } from "lodash"

export function getContributorDescriptors(composition = false) {
  return `
id
modelProviderId
bloombergTicker
${composition ? "internalId" : ""}
assetClass
assetSubclass
description
country
currency
`
}

export function getSimulateActionsNode(parameters = []) {
  return `simulateActions (positionSet: $positionSet, on: $on, actions: $actions, batchId: $intradayBatchId, ${getUsageParameters(parameters)})`
}

export function getQueryParameters(parameters = [], isGrouped = false) {
  return `
  $modelId: String!
  $on: Date!
  $positionSet: PositionSetInput!
  $actions: [ActionInput!]
  $intradayBatchId: Id
  ${!isGrouped ? "" : `
    $groupBy: ContributorGroupType!
    $groupById: String
    $tier: String
  `}
  ${chain(parameters)
    .compact()
    .map("query")
    .join(" ")
    .value()}`
}

export function getUsageParameters(parameters = [], addParentheses = false) {
  const usageString = chain(parameters)
    .compact()
    .map("usage")
    .join(" ")
    .value()

  return addParentheses && !isEmpty(usageString) ? `(${usageString})` : usageString
}

export function getActionsBaseParameter(options = {}) {
  const { actionsBase } = options

  if (actionsBase) {
    return {
      query: "$actionsBase: [PositionSetInput!]",
      usage: "actionsBase: $actionsBase",
    }
  }

  return null
}

export function getIncludeNotHeldParameter(options = {}) {
  const { isActive } = options
  if (isActive) {
    return {
      query: "$includeNotHeld: Boolean",
      usage: "includeNotHeld: $includeNotHeld",
    }
  }
  return null
}

export function getAttributionMethodParameter(options = {}) {
  const { isIntraday, isOpPro } = options
  // Intraday does not support attribution method
  if (!isIntraday && !isOpPro) {
    return {
      query: "$attributionMethod: RiskContributorAttributionMethod",
      usage: "attributionMethod: $attributionMethod",
    }
  }
  return null
}

export function getHorizonParameter(forecast, options = {}) {
  const { isOpPro, isIntraday } = options
  const forecastId = get(forecast, "forecast")
  const useForecastContributors = !isIntraday && (forecastId || (!isOpPro && !forecastId))
  if (!useForecastContributors) {
    return null
  }
  return {
    query: "$horizon: Int",
    usage: "horizon: $horizon",
  }
}

export function getBetaInstrumentParameter(betaInstrument, options = {}) {
  const { isIntraday } = options
  if (!betaInstrument || isIntraday) { return null }
  return {
    query: "$betaInstrument: PositionSetInput!",
    usage: "instrument: $betaInstrument",
  }
}

export function getContentSetParameter(contentSet) {
  if (!contentSet) { return null }
  return {
    query: "$contentSetId: String!",
    usage: "contentSetId: $contentSetId",
  }
}
export function getForecastIdParameter(forecastAndHorizon) {
  const forecast = get(forecastAndHorizon, "forecast")
  if (!forecast) { return null }

  // Custom Forecast
  return {
    query: "$forecastId: ShortId!",
    usage: "forecastId: $forecastId",
  }
}

export function getForecastSecuritiesParameter(options = {}) {
  const { forecastSecurities, isOpPro } = options
  // OpPro can only use custom forecast so will always be passing forecastId and never forecastSecurities
  if (!forecastSecurities || isOpPro) { return null }
  return {
    query: "$forecastSecurities: [ForecastSecurityInput!]",
    usage: "forecastSecurities: $forecastSecurities",
  }
}

export function getAnalyticsSegmentParameter({ analyticsSegment }) {
  if (analyticsSegment) {
    return {
      query: "$analyticsSegment: Segment",
      usage: "analyticsSegment: $analyticsSegment",
    }
  }

  return null
}

export function getAnalyticsOutputParameter({ analyticsSegment, isActive, expand }) {
  if (isActive || analyticsSegment || expand) {
    return {
      query: "$analyticsOutput: SimulateActionsAnalyticsInput",
      usage: "analyticsOutput: $analyticsOutput",
    }
  }

  return null
}

export function getAnalyticsExpandParameter({ analyticsExpand }) {
  if (analyticsExpand) {
    return {
      query: "$analyticsExpand: Boolean",
      usage: "analyticsExpand: $analyticsExpand",
    }
  }

  return null
}

export function getQueryId(props, keys = []) {
  return chain(props)
    .pick(keys)
    .orderBy([(value) => isObject(value)], ["asc"])
    .map((value) => isObject(value) ? JSON.stringify(value) : value)
    .join("-")
    .value()
}

export function getVariables(props, query) {
  const { selectedDate, portfolioAliasOrId, isActive, base, betaInstrument, forecastAndHorizon, forecastSecurities, selectedContentSet, groupingOptions, isOpPro, isIntraday, simulateActions, expand, analyticsSegment } = props
  const gqlVariables = chain(query.match(/\$\w*:/gi))
    .map(variable => variable.substr(1, variable.length - 2))
    .value()

  const positionSet = {
    id: portfolioAliasOrId,
    type: "PORTFOLIO",
  }
  // Not all gql variables have a 1:1 match with the props/state variables so we build an object with the proper mapping
  // Then we introspect the gql query to find which variables are actually required and only pick these to avoid sending unnecessary data
  return pick({
    ...pick(props, ["modelId", "actionsBase", "intradayBatchId", "attributionMethod"]),
    actions: simulateActions,
    on: selectedDate,
    positionSet,
    base: isActive ? [base] : undefined,
    includeNotHeld: isActive,
    contentSetId: get(selectedContentSet, "id"),
    betaInstrument: pick(betaInstrument, ["type", "id"]),
    ...computeAnalyticsOutput({ isActive, base, analyticsSegment, expand }),
    ...computeForecastVariables(forecastAndHorizon, forecastSecurities, isOpPro, isIntraday),
    ...groupingOptions,
  }, gqlVariables)
}

export function computeAnalyticsOutput({ isActive, base, analyticsSegment, expand }) {
  if (!isActive && !analyticsSegment && !expand) { return {} }

  return {
    analyticsOutput: {
      ...(analyticsSegment && { segment: analyticsSegment }),
      ...(expand && { expand }),
      base: isActive ? base : null,
    },
  }
}

export function computeForecastVariables(forecastAndHorizon, forecastSecurities, isOpPro, isIntraday) {
  if (isIntraday) { return {} }

  const { horizon, forecast } = forecastAndHorizon ?? {}
  return {
    horizon,
    ...(forecast && { forecastId: forecast.id }),
    ...(forecastSecurities && !forecast && !isOpPro && { forecastSecurities }),
  }
}
